import * as React from 'react'
import { ChevronsUpDown, Check } from 'lucide-react'
import { useFormContext } from 'react-hook-form'
import { Button } from '@/components/ui/button'
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import { cn } from '@/lib/utils'
import { usePlans } from '@/features/plans/api/get-plans'

type PlanComboboxProps = {
    label?: string
    name: string
    placeholder?: string
}

export default function PlanCombobox({ label = 'Plan', name, placeholder = 'Select plan' }: PlanComboboxProps) {
    const form = useFormContext()
    const { data, isLoading } = usePlans({})
    const plans = React.useMemo(() => {
        const arr = (data as unknown as any[]) ?? []
        return arr.map((p) => ({
            label: p.name ?? p.planSku ?? p.id ?? p.planId,
            value: p.planId ?? p.id,
        }))
    }, [data])
    const [open, setOpen] = React.useState(false)

    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => (
                <FormItem className='flex flex-col w-full'>
                    {label && <FormLabel>{label}</FormLabel>}
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <Button
                                    variant='outline'
                                    role='combobox'
                                    className={cn('justify-between', !field.value && 'text-muted-foreground')}
                                >
                                    {field.value ? plans.find((a) => a.value === field.value)?.label : (isLoading ? 'Loading…' : placeholder)}
                                    <ChevronsUpDown className='opacity-50' />
                                </Button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className='p-0'>
                            <Command>
                                <CommandInput placeholder='Search plans…' className='h-9' />
                                <CommandList>
                                    <CommandEmpty>No plans found.</CommandEmpty>
                                    <CommandGroup>
                                        {plans.map((plan) => (
                                            <CommandItem
                                                key={plan.value}
                                                value={plan.label}
                                                onSelect={() => {
                                                    form.setValue(name as any, plan.value, { shouldDirty: true })
                                                    setOpen(false)
                                                }}
                                            >
                                                {plan.label}
                                                <Check className={cn('ml-auto', plan.value === field.value ? 'opacity-100' : 'opacity-0')} />
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                    <FormMessage />
                </FormItem>
            )}
        />
    )
}


